import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import {useIntl} from 'react-intl';

import DatePicker from '@stubhub/react-date-picker';
import DropdownList from '@stubhub/react-dropdown-list';
import '@stubhub/react-font-icons';
import {connect} from '@stubhub/react-store-provider';
import withQueryParams from '@stubhub/react-with-query-params';

import controller from './controller';
import {viewCalendar} from './icons';
import {altMessages} from './messages';
import {qualifiedDate, dateParam} from './query-params';
import {dateFilterEndSelector, dateFilterKeySelector, dateFilterSelector, dateFilterStartSelector} from './selectors';
import {
  K_ALL,
  K_CHOOSE,
  K_THIS_MONTH,
  K_THIS_WKD,
  K_TODAY,
  formatDate,
  getStartDate,
  getEndDate,
  getItemList,
  getItemByKey,
} from './utils';

import '../styles/index.scss';

const DateFilter = ({
  useAltMsgs,
  selected = K_ALL,
  dateFilter = {},
  inPillBar = false,
  onClick,
  useQueryParams = true,
  replaceQuery,
  onSelection,
  className,
  dropDownToggle,
  iconColor,
  query,
  draggable = false,
  useBackdrop = false,
  isDatePickerEnabled,
  areQueryParamsEnabled,
  currentLocale,
}) => {
  const intl = useIntl();

  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickDateDeferred, setPickDateDeferred] = useState(null);
  const iconCls = inPillBar ? 'DateFilter__iconCalendar' : 'sh-iconset-calendar';
  const icon = inPillBar ? viewCalendar : null;
  const itemList = getItemList(isDatePickerEnabled, !!useAltMsgs);
  const dateFilterItems = itemList.map((key) => {
    return {key, name: intl.formatMessage(altMessages[key])};
  });
  const [selectedOption, setSelectedOption] = useState(() => {
    const defaultItem = getItemByKey(selected, dateFilterItems);
    const allItem = getItemByKey(K_ALL, dateFilterItems);

    return defaultItem || allItem;
  });
  const [selectedName, setSelectedName] = useState(null);

  const actualUseQueryParams = areQueryParamsEnabled && useQueryParams;

  const dateSelection = ({key, name, start, end}) => {
    const selProps = {key, name, start, end};
    onSelection && onSelection(selProps);

    return selProps;
  };

  useEffect(() => {
    if (query && query.pbD) {
      dateSelection(qualifiedDate(query.pbD));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dateFilter && dateFilter.key) {
      const option = getItemByKey(dateFilter.key, dateFilterItems);
      if (dateFilter.key === K_CHOOSE) {
        setStart(dateFilter.start);
        setEnd(dateFilter.end);
        setSelectedName(parseSelectionDisplay(dateFilter));
      }
      setSelectedOption(option);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  useEffect(() => {
    if (pickDateDeferred && typeof pickDateDeferred !== 'boolean') {
      const {key, name, start, end} = pickDateDeferred || {};
      if (key === K_CHOOSE && start && end) {
        handleDateSelection(dateSelection({key, name, start, end}));
        setShowDatePicker(false);
        setPickDateDeferred(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickDateDeferred]);

  const handleDateSelection = (dateTime) => {
    const {key} = dateTime;
    if (key) {
      actualUseQueryParams && replaceQuery({pbD: dateParam(dateTime)});
    }
  };

  const pickDate = (from, to) => {
    if (pickDateDeferred) {
      const item = getItemByKey(K_CHOOSE, dateFilterItems);
      setPickDateDeferred({...item, start: from, end: to});
      setStart(from);
      setEnd(to);
    }
  };

  const handleSelectionListOption = (item) => {
    if (!item) {
      return;
    }

    const {key} = item;

    if (key === K_CHOOSE) {
      if (!showDatePicker) {
        setShowDatePicker(true);
        setPickDateDeferred(true);
      }
    } else {
      handleDateSelection({key});
      onSelection({key});
    }
  };

  const parseSelectionDisplay = ({key, name}) => {
    if (key === K_CHOOSE && start && end) {
      return `${formatDate(start, currentLocale)} - ${formatDate(new Date(end), currentLocale)}`;
    }

    return name;
  };

  const handleDatePickerClose = () => {
    setShowDatePicker(false);
  };

  const handleClick = (e) => {
    onClick && onClick(e);
  };

  return (
    <div className={cs('sec-date-filter', {'sec-date-filter--all': selectedOption?.key === K_ALL}, className)}>
      <DropdownList
        className="date-filter"
        selectionTitle={selectedName}
        selected={selectedOption}
        items={dateFilterItems}
        onSelection={handleSelectionListOption}
        parseSelectionDisplay={parseSelectionDisplay}
        onClick={handleClick}
        useCheckMark
        dropDownToggle={dropDownToggle}
        draggable={draggable}
        useBackdrop={useBackdrop}
        iconCls={iconCls}
        icon={icon}
        iconColor={iconColor}
      />
      {showDatePicker ? (
        <DatePicker
          onSelection={(from, to) => pickDate(from, to)}
          onClose={handleDatePickerClose}
          from={start}
          to={end}
          draggable={draggable}
          onClick={handleClick}
        />
      ) : null}
    </div>
  );
};

DateFilter.propTypes = {
  /**
   * The current date filter object.
   */
  dateFilter: PropTypes.object,

  /**
   * Flag indicating if the component is in the pill bar.
   */
  inPillBar: PropTypes.bool,

  /**
   * Callback function when an item is clicked.
   */
  onClick: PropTypes.func,

  /**
   * Flag indicating if query parameters should be used.
   */
  useQueryParams: PropTypes.bool,

  /**
   * Function to replace the query parameters.
   */
  replaceQuery: PropTypes.func.isRequired,

  /**
   * Callback function when an item is selected.
   */
  onSelection: PropTypes.func.isRequired,

  /**
   * Additional class names for the component.
   */
  className: PropTypes.string,

  /**
   * Wheter toggle the dropdown or not.
   */
  dropDownToggle: PropTypes.bool,

  /**
   * Color of the icon.
   */
  iconColor: PropTypes.string,

  /**
   * Flag indicating if alternative messages should be used.
   */
  useAltMsgs: PropTypes.bool,

  /**
   * The selected key.
   */
  selected: PropTypes.string,

  /**
   * The query object.
   */
  query: PropTypes.object,

  /**
   * Flag indicating if the component is draggable.
   */
  draggable: PropTypes.bool,

  /**
   * Flag indicating if the component should use a backdrop.
   */
  useBackdrop: PropTypes.bool,

  /**
   * Flag indicating if the date picker is enabled.
   */
  isDatePickerEnabled: PropTypes.bool,

  /**
   * Flag indicating if query parameters are enabled.
   */
  areQueryParamsEnabled: PropTypes.bool,

  /**
   * The current locale.
   */
  currentLocale: PropTypes.string,
};

export const WithoutQueryParams = connect(controller)(DateFilter);
export default connect(controller)(withQueryParams({pbD: 'pbD'})(DateFilter));

export const selectors = {
  dateFilter: dateFilterSelector,
  endDate: dateFilterEndSelector,
  key: dateFilterKeySelector,
  startDate: dateFilterStartSelector,
};

export const keys = {
  K_ALL,
  K_CHOOSE,
  K_THIS_MONTH,
  K_THIS_WKD,
  K_TODAY,
};

export const utils = {
  formatDate,
  getStartDate,
  getEndDate,
};

export {default as messages} from './messages';
